import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, Crown, Star, Heart, Gem, Palette, Fingerprint, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * EducationalCarousel - Sección educativa sobre tipos de perfumes
 */
const EducationalCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Inject Google Font
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const slides = [
    {
      id: 1,
      title: "¿NICHO O DISEÑADOR?",
      titleOnly: true,
      accentColor: "#a855f7"
    },
    {
      id: 2,
      title: "PERFUMES DE DISEÑADOR",
      subtitle: "Aromas creados para conquistar.",
      lines: [
        "Fragancias de grandes casas de moda, pensadas para gustar, destacar y acompañarte todos los días.",
        "Suelen ser versátiles, fáciles de llevar y con aromas que resultan familiares y atractivos.",
        "Perfectos si buscas un perfume que genere cumplidos desde el primer encuentro."
      ],
      icons: [
        { icon: Crown, label: "Prestigio" },
        { icon: Star, label: "Cumplidos" },
        { icon: Award, label: "Versátil" }
      ],
      accentColor: "#f59e0b"
    },
    {
      id: 3,
      title: "PERFUMES NICHO",
      subtitle: "Cuando oler diferente se convierte en personalidad.",
      lines: [
        "La perfumería nicho apuesta por la creatividad, ingredientes especiales y composiciones menos convencionales.",
        "Son aromas con mayor carácter, capaces de sorprender y contar una historia diferente sobre tu piel.",
        "Perfectos para quienes no quieren oler como todos los demás."
      ],
      icons: [
        { icon: Gem, label: "Exclusivo" },
        { icon: Palette, label: "Creativo" },
        { icon: Fingerprint, label: "Único" }
      ],
      accentColor: "#06b6d4"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentData = slides[currentSlide];

  return (
    <section className="relative py-3 md:py-4 bg-brand-black overflow-hidden">
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 opacity-20 transition-all duration-700"
        style={{
          background: `radial-gradient(ellipse at center, ${currentData.accentColor}15 0%, transparent 60%)`
        }}
      />

      <div className="max-w-4xl mx-auto px-4 relative z-10">
        {/* Carousel Container */}
        <div className="relative">
          {/* Slides */}
          <div className="relative h-[260px] md:h-[220px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex flex-col justify-center text-center px-6 md:px-12 py-4 md:py-5 rounded-xl border border-white/10 bg-white/[0.02]"
                style={{
                  boxShadow: `0 0 40px -10px ${currentData.accentColor}30, inset 0 1px 0 0 rgba(255,255,255,0.05)`
                }}
              >
                {/* Title with fashion neon effect */}
                <h3
                  className="text-4xl md:text-5xl lg:text-6xl font-light italic mb-1 tracking-wide"
                  style={{ 
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    background: `linear-gradient(90deg, ${currentData.accentColor} 0%, #ffffff 25%, ${currentData.accentColor} 50%, #ffffff 75%, ${currentData.accentColor} 100%)`,
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'shimmer 12s ease-in-out infinite',
                    textShadow: `0 0 30px ${currentData.accentColor}40`,
                    filter: `drop-shadow(0 0 10px ${currentData.accentColor}30)`
                  }}
                >
                  {currentData.title}
                </h3>
                
                {/* CSS for shimmer animation */}
                <style>{`
                  @keyframes shimmer {
                    0% { background-position: 200% center; }
                    100% { background-position: -200% center; }
                  }
                `}</style>
                
                {!currentData.titleOnly && (
                  <>
                    {/* Subtitle */}
                    <p className="text-white/90 font-medium mb-3 text-sm md:text-base italic">
                      {currentData.subtitle}
                    </p>

                    {/* Lines */}
                    <div className="space-y-0.5 mb-3">
                      {currentData.lines.map((line, idx) => (
                        <p key={idx} className="text-white/60 text-xs md:text-sm leading-snug max-w-2xl mx-auto">
                          {line}
                        </p>
                      ))}
                    </div>

                    {/* Icons Row */}
                    <div className="flex justify-center items-center gap-5 md:gap-8">
                      {currentData.icons.map((item, idx) => {
                        const IconComponent = item.icon;
                        return (
                          <div key={idx} className="flex items-center gap-1.5">
                            <IconComponent
                              size={14}
                              style={{ color: currentData.accentColor }}
                            />
                            <span className="text-[10px] text-white/40 uppercase tracking-wider">
                              {item.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft size={18} className="text-white/50" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="Siguiente"
          >
            <ChevronRight size={18} className="text-white/50" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-3">
          {slides.map((slide, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{
                backgroundColor: index === currentSlide ? currentData.accentColor : 'rgba(255,255,255,0.2)',
                width: index === currentSlide ? '24px' : '8px'
              }}
              aria-label={`Ir a slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EducationalCarousel;
